export class SmartItem {
  key = ""; // 唯一标识
  value = ""; // 显示值
  rawData = null; // 原始数据
  constructor(options = {}) {
    Object.assign(this, options);
  }
}

export const SmartControlMixin = {
  emits: ["input"],
  props: {
    loadMethod: { type: Function, required: false },
    options: { type: Array, default: null },
    value: null,
    multiple: { type: Boolean, default: false },
    // 分页相关
    page: { type: Number, default: 1 }, // 对外暴露page
    pageSize: { type: Number, default: 20 }, // 对外暴露pageSize
    pageable: { type: Boolean, default: false },
    // 分页字段映射，允许自定义参数名，如 { page: 'skip', pageSize: 'limit' } 或 { page: 'f1', pageSize: 'f2' }
    pageFieldMap: {
      type: Object,
      default: () => ({ page: 'page', pageSize: 'pageSize' })
    },
    autoLoad: { type: Boolean, default: true },
    fetchParams: { type: Object, default: () => ({}) },
    queryParameters: { type: Object, default: () => ({}) },
    likeFields: { type: Array, default: () => [] },
    autoSelect: { type: Boolean, default: true },
    addAllOption: { type: [Boolean, Object], default: false },
    displayField: { type: String, default: "name" },
    valueField: { type: String, default: "id" },
    valueType: { type: String, default: "auto" },
    responseListField: { type: String, default: "list" },
  },
  data() {
    return {
      loading: false,
      list: [],
      allList: [],
      selected: this.multiple ? [] : null,
      currentPage: this.page ?? 1,
      total: 0,
      emptyValue: this.computeInitialEmptyValue(),
    };
  },
  watch: {
    fetchParams: {
      handler() {
        this.loadList();
      },
      deep: true,
    },
    options: {
      handler() {
        if (this.options) {
          this.allList = this.modelValueToItem(this.options);
          this.initializeSelectedValue();
        }
      },
      immediate: true,
    },
    queryParameters: {
      handler() {
        if (this.isOfflineMode()) {
          this.applyLocalFilter();
        } else {
          this.loadList();
        }
      },
      deep: true,
    },
    value: {
      handler(val) {
        this.updateEmptyValueCandidate(val);
        this.selected = val;
      },
      immediate: true,
    },
  },
  methods: {
    computeInitialEmptyValue() {
      if (this.multiple) {
        return Array.isArray(this.value) && this.value.length === 0
          ? []
          : [];
      }
      if (this.value === undefined) return undefined;
      if (this.value === null) return null;
      if (this.value === "") return "";
      return null;
    },
    updateEmptyValueCandidate(value) {
      if (this.multiple) {
        if (Array.isArray(value) && value.length === 0) {
          this.emptyValue = [];
        }
        return;
      }
      if (value === undefined || value === null || value === "") {
        this.emptyValue = value;
      }
    },
    getEmptyValueForClear() {
      if (this.multiple) {
        return [];
      }
      if (this.emptyValue === undefined) return undefined;
      if (this.emptyValue === null) return null;
      if (this.emptyValue === "") return "";
      return null;
    },
    normalizedValueType() {
      return (this.valueType || "auto").toLowerCase();
    },
    isArrayValueType() {
      const vt = this.normalizedValueType();
      return vt.startsWith("array<") && vt.endsWith(">");
    },
    arrayInnerValueType() {
      if (!this.isArrayValueType()) return "";
      return this.normalizedValueType().slice(6, -1);
    },
    isObjectLikeValueType() {
      const vt = this.normalizedValueType();
      return vt === "object" || vt === "array<object>";
    },
    isPrimitiveOption(option) {
      const t = typeof option;
      return (
        option === null || t === "string" || t === "number" || t === "boolean"
      );
    },
    formatPrimitiveLabel(value) {
      if (value === undefined) return "undefined";
      if (value === null) return "null";
      if (typeof value === "boolean") return value ? "True" : "False";
      return String(value);
    },
    extractValueKey(value) {
      if (value && typeof value === "object" && this.valueField in value) {
        return value[this.valueField];
      }
      return value;
    },
    castValueType(value, typeOverride) {
      if (value === undefined || value === null) return value;
      const vt = (typeOverride || this.normalizedValueType()).toLowerCase();
      if (vt.startsWith("array<") && vt.endsWith(">")) {
        const innerType = vt.slice(6, -1);
        return this.castValueType(value, innerType);
      }
      if (vt === "number") return Number(value);
      if (vt === "string") return value !== undefined && value !== null ? String(value) : value;
      if (vt === "boolean") return value === "false" ? false : Boolean(value);
      if (vt === "object") return value;
      return value;
    },
    optionToSmartItem(option, index) {
      // Always cast key to string if valueType is string
      const isStringType = this.normalizedValueType() === 'string';
      if (this.isPrimitiveOption(option)) {
        const key = isStringType ? String(option) : option;
        return new SmartItem({
          key,
          value: this.formatPrimitiveLabel(option),
          rawData: option,
        });
      }
      const hasValueField =
        option && Object.prototype.hasOwnProperty.call(option, this.valueField);
      const hasIdField =
        option && Object.prototype.hasOwnProperty.call(option, "id");
      const hasValueProp =
        option && Object.prototype.hasOwnProperty.call(option, "value");
      let key;
      if (hasValueField) {
        key = option[this.valueField];
      } else if (hasIdField) {
        key = option.id;
      } else if (hasValueProp) {
        key = option.value;
      } else {
        key = index;
      }
      if (isStringType) key = String(key);
      const label =
        (option && option[this.displayField]) ??
        option?.label ??
        option?.name ??
        this.formatPrimitiveLabel(key);
      return new SmartItem({
        key,
        value: label,
        rawData: option,
      });
    },
    modelToKeys(value) {
      const vt = this.normalizedValueType();
      if (vt.startsWith("array<")) {
        if (!Array.isArray(value)) return [];
        const innerType = this.arrayInnerValueType();
        if (innerType === "object") {
          return value.map((item) => this.extractValueKey(item));
        }
        return value.map((item) => this.castValueType(item, innerType));
      }
      if (vt === "object") {
        return this.extractValueKey(value);
      }
      // Always cast to string if valueType is string
      if (vt === 'string') {
        return value !== undefined && value !== null ? String(value) : value;
      }
      return this.castValueType(value, vt);
    },
    keysToModel(keys) {
      const vt = this.normalizedValueType();
      if (vt.startsWith("array<")) {
        if (!Array.isArray(keys)) return [];
        const innerType = this.arrayInnerValueType();
        if (innerType === "object") {
          return keys
            .map((key) => {
              const found = (this.list || []).find((item) => item.key === key);
              return found ? found.rawData : null;
            })
            .filter(Boolean);
        }
        return keys.map((key) => this.castValueType(key, innerType));
      }
      if (vt === "object") {
        const found = (this.list || []).find((item) => item.key === keys);
        return found ? found.rawData : this.value || {};
      }
      // Always cast to string if valueType is string
      if (vt === 'string') {
        return keys !== undefined && keys !== null ? String(keys) : keys;
      }
      return this.castValueType(keys, vt);
    },
    async loadList() {
      if (this.isOfflineMode()) {
        this.applyLocalFilter();
        return;
      }
      if (!this.loadMethod) return;
      this.loading = true;
      try {
        let params = { ...this.fetchParams, ...this.queryParameters };
        if (Array.isArray(this.likeFields)) {
          this.likeFields.forEach((field) => {
            if (
              params[field] &&
              typeof params[field] === "string" &&
              !params[field].startsWith("%")
            ) {
              params[field] = `%${params[field]}%`;
            }
          });
        }
        // 分页参数适配
        const pageField = this.pageFieldMap.page || 'page';
        const pageSizeField = this.pageFieldMap.pageSize || 'pageSize';
        params[pageField] = this.page;
        params[pageSizeField] = this.pageSize;

        const res = await this.loadMethod(params);
        let dataList = [];
        if (Array.isArray(res)) {
          dataList = res;
        } else if (Array.isArray(res?.[this.responseListField])) {
          dataList = res[this.responseListField];
        } else {
          for (const key in res || {}) {
            if (Array.isArray(res[key])) {
              dataList = res[key];
              break;
            }
          }
        }
        if (
          Array.isArray(dataList) &&
          !res?.total &&
          !res?.page &&
          !res?.pageSize
        ) {
          this.allList = this.modelValueToItem(dataList);
          this.applyLocalFilter();
        } else {
          this.list = this.modelValueToItem(dataList);
          this.total = res?.total || dataList.length;
          this.initializeSelectedValue();
        }
      } catch (error) {
        this.$emit("error", error);
      } finally {
        this.loading = false;
      }
    },
    isOfflineMode() {
      if (this.options) return true;
      if (this.allList && this.allList.length) return true;
      return false;
    },
    applyLocalFilter() {
      let filtered = (this.allList || []).slice();
      const filters = { ...this.queryParameters };
      const likeFields = this.likeFields || [];
      filtered = filtered.filter((item) => {
        return Object.keys(filters).every((field) => {
          const filterValue = filters[field];
          if (
            filterValue === undefined ||
            filterValue === null ||
            filterValue === "" ||
            (Array.isArray(filterValue) && filterValue.length === 0)
          ) {
            return true;
          }
          const rawValue = item.rawData ? item.rawData[field] : item[field];
          if (typeof filterValue === "string" && likeFields.includes(field)) {
            const pattern = filterValue.replace(/%/g, "").toLowerCase();
            return String(rawValue ?? "")
              .toLowerCase()
              .includes(pattern);
          }
          return rawValue === filterValue;
        });
      });
      this.list = filtered;
      this.total = filtered.length;
      this.initializeSelectedValue();
    },
    handleSelect(item) {
      const isObjectMode = this.isObjectLikeValueType();
      let value;
      if (isObjectMode) {
        value = item.rawData || item;
      } else {
        value = item.key !== undefined ? item.key : item[this.valueField];
        value = this.castValueType(value);
      }
      if (this.multiple) {
        if (!Array.isArray(this.selected)) this.selected = [];
        const equals = (a, b) => {
          if (isObjectMode) {
            if (this.valueField && a && b) {
              return a[this.valueField] === b[this.valueField];
            }
            return JSON.stringify(a) === JSON.stringify(b);
          }
          return a === b;
        };
        const index = this.selected.findIndex((entry) => equals(entry, value));
        if (index > -1) {
          this.selected.splice(index, 1);
        } else {
          this.selected.push(value);
        }
      } else {
        this.selected = value;
      }
      this.$emit('select', item);
      this.$emit("input", this.selected);
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.loadList();
    },
    handleClear() {
      const clearedValue = this.getEmptyValueForClear();
      this.selected = this.multiple ? [] : clearedValue;
      this.$emit("input", this.selected);
    },
    autoSelectFirstItem() {
      if (!Array.isArray(this.list) || this.list.length === 0) return;
      if (this.isObjectLikeValueType()) {
        return this.multiple ? [this.list[0].rawData] : this.list[0].rawData;
      }
      const casted = this.castValueType(this.list[0].key);
      return this.multiple ? [casted] : casted;
    },
    resolveValueForSelection(value, innerType) {
      if (value === undefined || value === null) return null;
      if (innerType === "object" || this.isObjectLikeValueType()) {
        const key = this.extractValueKey(value);
        const found = this.list.find((item) => item.key === key);
        return found ? found.rawData : value;
      }
      const primitive = this.castValueType(
        this.extractValueKey(value),
        innerType
      );
      const found = this.list.find((item) => item.key === primitive);
      return found ? found.key : primitive;
    },
    initializeSelectedValue() {
      if (
        !this.autoSelect ||
        !Array.isArray(this.list) ||
        this.list.length === 0
      ) {
        return;
      }
      const hasSelection = this.multiple
        ? Array.isArray(this.selected) && this.selected.length > 0
        : this.selected !== undefined &&
          this.selected !== null &&
          (this.selected !== "" || this.value === "");
      if (hasSelection) return;

      if (this.multiple) {
        const innerType =
          this.arrayInnerValueType() || this.normalizedValueType();
        const values = Array.isArray(this.value) ? this.value : [];
        this.selected = values
          .map((val) => this.resolveValueForSelection(val, innerType))
          .filter((entry) => entry !== null && entry !== undefined);
      } else if (
        this.value !== undefined &&
        this.value !== null &&
        this.value !== ""
      ) {
        const mapped = this.resolveValueForSelection(
          this.value,
          this.normalizedValueType()
        );
        this.selected = mapped;
      } else {
        if (this.multiple) {
          this.selected = Array.isArray(this.value) ? [...this.value] : [];
        } else {
          this.selected = this.value;
        }
      }
      this.$emit("input", this.selected);
    },
    modelValueToItem(array = []) {
      const items = array.map((item, index) =>
        this.optionToSmartItem(item, index)
      );
      if (this.addAllOption) {
        if (typeof this.addAllOption === "object") {
          items.unshift(
            new SmartItem({
              key: this.addAllOption.key,
              value: this.addAllOption.value,
              rawData: null,
            })
          );
        } else {
          items.unshift(
            new SmartItem({ key: undefined, value: "all", rawData: null })
          );
        }
      }
      return items;
    },
    getDisplay(item) {
      if (this.isObjectLikeValueType()) {
        return item.rawData ? item.rawData[this.displayField] : "";
      }
      if (
        ["number", "string", "boolean"].includes(this.normalizedValueType())
      ) {
        return item.key;
      }
      return item.rawData;
    },
  },
  created() {
    if (Array.isArray(this.options) && this.options.length > 0) {
      this.list = this.modelValueToItem(this.options);
      this.total = this.list.length;
      this.initializeSelectedValue();
    } else if (this.autoLoad) {
      this.loadList();
    }
  },
};
